// Fractional indexing for Binder-list order (the list-reorder spec §2).
//
// A `position` is a non-empty string over the ordered base-62 ASCII alphabet
// below (digits < uppercase < lowercase, strictly ascending bytes). It denotes a
// base-62 fraction in (0, 1); plain lexicographic (byte) comparison of two
// positions equals numeric order, and is identical in JS / Swift / Kotlin for
// ASCII-only strings — so order needs no locale/Unicode collation anywhere.
//
// `keyBetween(a, b)` returns a position strictly between `a` and `b`
// (lexicographically), where `null` means an open bound (0 below, 1 above). Each
// drag generates ONE key on the acting client; other clients only read + compare
// it, so byte-identical generation across platforms is a bonus, not a
// correctness requirement — convergence rests on compare + the position LWW
// merge. The algorithm is deterministic (no jitter) so tests/vectors are stable.
//
// Mechanism: represent the lower bound (default 0) and upper bound (default 1.0)
// as base-62 fractions and return `(lower + upper) / 2` — always strictly
// between when lower < upper. Keys may lengthen by a digit per midpoint, which is
// irrelevant at checklist sizes.

export const BASE62 =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const RADIX = BASE62.length; // 62

const digitOf = (ch: string): number => BASE62.indexOf(ch);

/** A base-62 fraction: an integer `carry` in {0,1} plus fractional `digits`.
 *  `carry === 1` represents 1.0 (the open upper bound); otherwise a value in [0,1). */
interface Fraction {
  carry: number;
  digits: number[];
}

/** Drop trailing zero digits so a key is canonical (numeric order == lexicographic). */
function strip(digits: number[]): number[] {
  let end = digits.length;
  while (end > 0 && digits[end - 1] === 0) end--;
  return digits.slice(0, end);
}

function parseLower(key: string | null): Fraction {
  if (key === null || key === '') return { carry: 0, digits: [] };
  return { carry: 0, digits: strip([...key].map(digitOf)) };
}

function parseUpper(key: string | null): Fraction {
  // Open upper bound → 1.0.
  if (key === null || key === '') return { carry: 1, digits: [] };
  return { carry: 0, digits: strip([...key].map(digitOf)) };
}

/** Sum of two fractions (result carry ≤ 2 since each input carry ≤ 1). */
function add(a: Fraction, b: Fraction): Fraction {
  const len = Math.max(a.digits.length, b.digits.length);
  const out = new Array<number>(len).fill(0);
  let carry = 0;
  for (let i = len - 1; i >= 0; i--) {
    const sum = (a.digits[i] ?? 0) + (b.digits[i] ?? 0) + carry;
    out[i] = sum % RADIX;
    carry = Math.floor(sum / RADIX);
  }
  return { carry: a.carry + b.carry + carry, digits: out };
}

/** Halve a fraction whose carry ≤ 2 → a value in [0,1) (resulting carry is 0). */
function halve(f: Fraction): number[] {
  const out: number[] = [];
  let rem = f.carry; // most-significant remainder flows down into the fraction
  for (const d of f.digits) {
    const cur = rem * RADIX + d;
    out.push(Math.floor(cur / 2));
    rem = cur % 2;
  }
  if (rem > 0) out.push(Math.floor((rem * RADIX) / 2)); // trailing .5 → one more digit
  return strip(out);
}

const encode = (digits: number[]): string => digits.map((d) => BASE62[d]).join('');

/**
 * A position strictly between `a` and `b` (lexicographically), `null` = open bound.
 * Requires `a < b` when both are given.
 */
export function keyBetween(a: string | null, b: string | null): string {
  if (a !== null && b !== null && a >= b) {
    throw new Error(`keyBetween: a must be < b (got ${JSON.stringify(a)}, ${JSON.stringify(b)})`);
  }
  const mid = halve(add(parseLower(a), parseUpper(b)));
  // mid is strictly between the (canonical) bounds and non-empty: (lower+upper)/2
  // with lower < upper always has a nonzero digit after stripping.
  return encode(mid);
}
